import { Reference } from '@approvalmax/types';
import { CheckboxEditor, Field, Popup, TextEditor, validators } from '@approvalmax/ui';
import { constants, selectors } from 'modules/common';
import { backend } from 'modules/data';
import { useDispatch, useSelector, useShallowEqualSelector } from 'modules/react-redux';
import { useOcrData } from 'pages/requestForm/hooks/useOcrData';
import { memo, useCallback, useState } from 'react';
import bemFactory from 'react-bem-factory';
import { useMount } from 'react-use';
import { notificationService } from 'services/notification';
import { isSortCodeValid } from 'shared/helpers';

import { createXeroContact } from '../../../actions';
import {
    EditableXeroContact,
    EditableXeroContactAddress,
    EditableXeroContactPhone,
    EditableXeroTrackingCategory,
} from '../../../data/xero/EditableXeroContact';
import { useGetRecognizedOcrAttachment } from '../../../hooks/useGetRecognizedOcrAttachment';
import { XeroContactCreationPopupData } from '../../../reducers/page/activePopup/xeroContactCreationPopupReducer';
import { getActivePopup, getRequest, isLoading } from '../../../selectors/pageSelectors';
import { getXeroContext, isValidXeroContactName } from '../../../selectors/xero';
import { AddressSection } from './components/AddressSection/AddressSection';
import { FinancialDetailsSection } from './components/FinancialDetailsSection/FinancialDetailsSection';
import { PhoneEditor } from './components/PhoneEditor/PhoneEditor';
import { PurchaseAndSalesSettingsSection } from './components/PurchaseAndSalesSettingsSection/PurchaseAndSalesSettingsSection';
import { TaxSection } from './components/TaxSection/TaxSection';
import { getContactFieldNameByIntegrationCode } from './XeroCreateContactPopup.helpers';
import { messages } from './XeroCreateContactPopup.messages';
import { Content, Header, Root, Row, RowGroup } from './XeroCreateContactPopup.styles';
import {
    AddressChangeFn,
    FieldChangeFn,
    PhoneChangeFn,
    XeroCreateContactPopupProps,
} from './XeroCreateContactPopup.types';

const { xeroConstants } = constants;

const bem = bemFactory.block('reqf-xero-contact-creation-popup');
const qa = bemFactory.qa('reqf-xero-contact-creation-popup');

export const XeroCreateContactPopup = memo<XeroCreateContactPopupProps>((props) => {
    const { className, title } = props;

    const dispatch = useDispatch();
    const { initialContact, loading, xeroContext, company } = useShallowEqualSelector((state) => {
        const xeroContext = getXeroContext(state);
        const request = getRequest(state);
        const company = selectors.company.getCompanyById(state, request.companyId);

        return {
            initialContact: getActivePopup<XeroContactCreationPopupData>(state).initialContact,
            loading: isLoading(state),
            xeroContext,
            company,
        };
    });

    const request = useSelector(getRequest);

    const ocrAttachment = useGetRecognizedOcrAttachment(request);

    const { data: ocrData } = useOcrData(request, ocrAttachment?.id);

    useMount(() => {
        if (!ocrData) return;

        const fieldName = getContactFieldNameByIntegrationCode(request.integrationCode);

        const processedInitialContact = {
            ...initialContact,
            name: ocrData.document?.[fieldName]?.name || initialContact.name,
            bankAccountDetails: ocrData.document?.[fieldName]?.bankAccountDetails || initialContact.bankAccountDetails,
            taxNumber: ocrData.document?.[fieldName]?.taxNumber || initialContact.taxNumber,
            defaultCurrency: ocrData.document?.currencyCode
                ? {
                      id: ocrData.document?.currencyCode,
                      text:
                          xeroContext.currencies.find((currency) => currency.id === ocrData.document?.currencyCode)
                              ?.text || '',
                  }
                : initialContact.defaultCurrency,
            billingAddress: {
                ...initialContact.billingAddress,
                address: ocrData.document?.[fieldName]?.address?.street || initialContact.billingAddress.address,
                city: ocrData.document?.[fieldName]?.address?.city || initialContact.billingAddress.city,
                region: ocrData.document?.[fieldName]?.address?.region || initialContact.billingAddress.region,
                country: ocrData.document?.[fieldName]?.address?.country || initialContact.billingAddress.country,
                postalCode:
                    ocrData.document?.[fieldName]?.address?.postalCode || initialContact.billingAddress.postalCode,
            },
        };

        setContact(processedInitialContact);
    });

    const [showErrors, setShowErrors] = useState(false);
    const [sameAsBillingAddressChecked, setSameAsBillingAddressChecked] = useState(false);
    const [contact, setContact] = useState(initialContact);

    const isUKCompany = company.integration?.countryCode === backend.OrganisationVersion.UK;

    const isInvalidEmail = !!(
        contact.email &&
        (!validators.isEmail(contact.email) ||
            !contact.email.trim() ||
            contact.email.length > xeroConstants.EMAIL_ADDRESS_MAX_LENGTH)
    );

    const isInvalidContact = !!(
        !contact.name ||
        !isValidXeroContactName(contact.name) ||
        (contact.name && (!contact.name?.trim() || contact.name.length > xeroConstants.CONTACT_NAME_MAX_LENGTH))
    );

    const isInvalidAccountNumber = !!(
        contact.accountNumber &&
        (!contact.accountNumber?.trim() || contact.accountNumber.length > xeroConstants.ACCOUNT_NUMBER_MAX_LENGTH)
    );

    const isInvalidFirstName = !!(
        contact.firstName &&
        (!contact.firstName?.trim() || contact.firstName.length > xeroConstants.FIRST_NAME_MAX_LENGTH)
    );

    const isInvalidLastName = !!(
        contact.lastName &&
        (!contact.lastName?.trim() || contact.lastName.length > xeroConstants.LAST_NAME_MAX_LENGTH)
    );

    const handleChangeTrackingCategory = useCallback(
        (
            trackingPropName: 'salesTrackingCategories' | 'purchasesTrackingCategories',
            index: number,
            newTrackingCategory: Reference,
            categoryName: string
        ) => {
            const categories: EditableXeroTrackingCategory[] = [...contact[trackingPropName]];

            const categoryWithName: EditableXeroTrackingCategory = {
                ...newTrackingCategory,
                name: categoryName,
            };

            categories[index] = categoryWithName;

            setContact((contact) => ({
                ...contact,
                [trackingPropName]: categories,
            }));
        },
        [contact]
    );

    const toggleSameAsBillingAddress = (sameAddress: boolean) => {
        setSameAsBillingAddressChecked(sameAddress);
    };

    const handleChangeContactReferencedType = useCallback(
        (propName: keyof EditableXeroContact, newValue: Reference) => {
            setContact((contact) => ({
                ...contact,
                [propName]: newValue,
            }));
        },
        []
    );

    const handleChangeContact = useCallback<FieldChangeFn>((propName) => {
        return (value) => {
            setContact((current) => ({
                ...current,
                [propName]: value,
            }));
        };
    }, []);

    const handleChangePhone = useCallback<PhoneChangeFn>((phonePropName, propName) => {
        return (value) => {
            const isValidPhone = value ? selectors.ui.isValidPhone(value) : true;

            if (isValidPhone) {
                setContact((current) => ({
                    ...current,
                    [phonePropName]: {
                        ...(current[phonePropName] as EditableXeroContactPhone),
                        [propName]: value,
                    },
                }));
            }
        };
    }, []);

    const handleChangeAddress = useCallback<AddressChangeFn>((addressPropName, propName) => {
        return (value) => {
            setContact((current) => ({
                ...current,
                [addressPropName]: {
                    ...(current[addressPropName] as EditableXeroContactAddress),
                    [propName]: value,
                },
            }));
        };
    }, []);

    const onDone = () => {
        setShowErrors(true);

        if (!contact.name) {
            notificationService.showErrorToast(messages.validationErrorText);

            return;
        }

        if (contact.name && !isValidXeroContactName(contact.name)) {
            notificationService.showErrorToast(messages.invalidContactName);

            return;
        }

        if (isUKCompany) {
            if (contact.sortCode && !isSortCodeValid(contact.sortCode)) {
                notificationService.showErrorToast(messages.invalidSortCode);

                return;
            }

            if (contact.sortCode && (!contact.bankAccountDetails || !/^\d{8}$/.test(contact.bankAccountDetails))) {
                notificationService.showErrorToast(messages.invalidBankAccountDetails);

                return;
            }
        }

        const newContact = { ...contact };

        if (sameAsBillingAddressChecked) {
            newContact.deliveryAddress = newContact.billingAddress;
        }

        if (isUKCompany && contact.sortCode) {
            newContact.bankAccountDetails = `${contact.sortCode}${contact.bankAccountDetails}`;
        }

        dispatch(createXeroContact(newContact));
    };

    return (
        <Root
            className={className}
            qa={qa()}
            title={title}
            buttons={
                <Popup.DefaultContent.Button
                    qa={qa('done-button')}
                    disabled={loading}
                    execute={onDone}
                    title={messages.doneButtonTitle}
                >
                    {messages.doneButtonText}
                </Popup.DefaultContent.Button>
            }
        >
            <Content>
                <Header>{messages.contactInformationHeader}</Header>

                <RowGroup>
                    <Row>
                        <Field title={messages.nameLabel} required>
                            <TextEditor
                                focusOnMount
                                qa={qa('name-editor')}
                                value={contact.name}
                                maxLength={xeroConstants.CONTACT_NAME_MAX_LENGTH}
                                onChange={handleChangeContact('name')}
                                placeholder={messages.namePlaceholder}
                                invalid={showErrors && isInvalidContact}
                            />
                        </Field>

                        <Field title={messages.accountNameLabel}>
                            <TextEditor
                                qa={qa('account-number-editor')}
                                value={contact.accountNumber || ''}
                                maxLength={xeroConstants.ACCOUNT_NUMBER_MAX_LENGTH}
                                onChange={handleChangeContact('accountNumber')}
                                placeholder={messages.accountNamePlaceholder}
                                invalid={showErrors && isInvalidAccountNumber}
                            />
                        </Field>
                    </Row>

                    <Row>
                        <Field title={messages.firstNameLabel}>
                            <TextEditor
                                qa={qa('first-name-editor')}
                                value={contact.firstName || ''}
                                maxLength={xeroConstants.FIRST_NAME_MAX_LENGTH}
                                onChange={handleChangeContact('firstName')}
                                placeholder={messages.firstNamePlaceholder}
                                invalid={showErrors && isInvalidFirstName}
                            />
                        </Field>

                        <Field title={messages.lastNameLabel}>
                            <TextEditor
                                qa={qa('last-name-editor')}
                                value={contact.lastName || ''}
                                maxLength={xeroConstants.LAST_NAME_MAX_LENGTH}
                                onChange={handleChangeContact('lastName')}
                                placeholder={messages.lastNamePlaceholder}
                                invalid={showErrors && isInvalidLastName}
                            />
                        </Field>
                    </Row>

                    <Row>
                        <Field title={messages.emailLabel}>
                            <TextEditor
                                qa={qa('email-editor')}
                                value={contact.email || ''}
                                maxLength={xeroConstants.EMAIL_ADDRESS_MAX_LENGTH}
                                onChange={handleChangeContact('email')}
                                placeholder={messages.emailPlaceholder}
                                invalid={showErrors && isInvalidEmail}
                            />
                        </Field>

                        <div />
                    </Row>
                </RowGroup>

                <RowGroup>
                    <Row>
                        <PhoneEditor
                            phone={contact.phone}
                            name='phone'
                            title={messages.phoneLabel}
                            showErrors={showErrors}
                            qa={qa}
                            onChange={handleChangePhone}
                        />

                        <PhoneEditor
                            phone={contact.fax}
                            name='fax'
                            title={messages.faxLabel}
                            showErrors={showErrors}
                            qa={qa}
                            onChange={handleChangePhone}
                        />
                    </Row>

                    <Row>
                        <PhoneEditor
                            phone={contact.mobile}
                            name='mobile'
                            title={messages.mobileLabel}
                            showErrors={showErrors}
                            qa={qa}
                            onChange={handleChangePhone}
                        />

                        <PhoneEditor
                            phone={contact.directDial}
                            name='directDial'
                            title={messages.directDialLabel}
                            showErrors={showErrors}
                            qa={qa}
                            onChange={handleChangePhone}
                        />
                    </Row>

                    <Row>
                        <Field title={messages.skypeNameLabel}>
                            <TextEditor
                                qa={qa('skype-editor')}
                                value={contact.skypeName || ''}
                                maxLength={xeroConstants.SKYPE_MAX_LENGTH}
                                onChange={handleChangeContact('skypeName')}
                                placeholder={messages.skypeNamePlaceholder}
                            />
                        </Field>
                    </Row>
                </RowGroup>

                <Header>{messages.billingAddressHeader}</Header>

                <AddressSection
                    address={contact.billingAddress}
                    name='billingAddress'
                    qa={qa}
                    onChange={handleChangeAddress}
                />

                <Header>{messages.deliveryAddressHeader}</Header>

                <CheckboxEditor
                    qa={qa('same-as-postal-address-checkbox')}
                    className={bem('same-address-checkbox')}
                    value={sameAsBillingAddressChecked}
                    onChange={toggleSameAsBillingAddress}
                >
                    {messages.sameAsBillingCheckboxLabel}
                </CheckboxEditor>

                {!sameAsBillingAddressChecked && (
                    <AddressSection
                        address={contact.deliveryAddress}
                        name='deliveryAddress'
                        qa={qa}
                        onChange={handleChangeAddress}
                    />
                )}

                <PurchaseAndSalesSettingsSection
                    contact={contact}
                    xeroContext={xeroContext}
                    qa={qa}
                    onChangeReferenceField={handleChangeContactReferencedType}
                    onTrackingCategoryChange={handleChangeTrackingCategory}
                />

                <TaxSection
                    contact={contact}
                    xeroContext={xeroContext}
                    qa={qa}
                    onChangeField={handleChangeContact}
                    onChangeReferenceField={handleChangeContactReferencedType}
                />

                <FinancialDetailsSection
                    company={company}
                    contact={contact}
                    xeroContext={xeroContext}
                    qa={qa}
                    showErrors={showErrors}
                    onChangeField={handleChangeContact}
                    onChangeReferenceField={handleChangeContactReferencedType}
                />
            </Content>
        </Root>
    );
});

XeroCreateContactPopup.displayName = 'XeroCreateContactPopup';
